import { whenSettled } from '@theme/defer-section';

const WARM_MARGIN = '100% 0px';

function prefersReducedData() {
  if (navigator.connection?.saveData) return true;
  return window.matchMedia('(prefers-reduced-data: reduce)').matches;
}

class DieuxProductVideo extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.autoplay = this.dataset.autoplay === 'true';
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.motionQuery = window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)');
    this.inView = !this.autoplay;
    this.warmRequested = false;

    this.desktopQuery.addEventListener('change', () => this.syncPlayers(), {
      signal: this.abortController.signal,
    });

    if (this.autoplay) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
        });
        this.syncPlayers();
      });
      this.observer.observe(this);

      this.warmObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.requestWarm();
        },
        { rootMargin: WARM_MARGIN }
      );
      this.warmObserver.observe(this);

      const { signal } = this.abortController;
      whenSettled().then(() => {
        if (!signal.aborted) this.requestWarm();
      });
    }

    customElements.whenDefined('video-player-component').then(() => {
      if (this.isConnected) this.syncPlayers();
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.warmObserver?.disconnect();
    this.warmObserver = null;
    this.abortController?.abort();
    this.players.forEach((player) => this.pausePlayer(player));
  }

  get players() {
    return Array.from(this.querySelectorAll('[data-product-video-player]'));
  }

  isActive(player) {
    const surface = player.dataset.productVideoPlayer;
    if (surface === 'all') return true;
    return (surface === 'desktop') === this.desktopQuery.matches;
  }

  pausePlayer(player) {
    if (typeof player.pause === 'function') player.pause();
  }

  requestWarm() {
    if (this.warmRequested || prefersReducedData() || this.motionQuery.matches) return;
    this.warmRequested = true;
    this.warmObserver?.disconnect();
    this.warmObserver = null;
    this.syncPlayers();
  }

  syncPlayers() {
    this.players.forEach((player) => {
      if (!this.isActive(player)) {
        this.pausePlayer(player);
        return;
      }

      if (!this.autoplay) return;

      if (this.inView) {
        if (typeof player.playAutoplay === 'function') player.playAutoplay();
      } else {
        if (this.warmRequested && typeof player.warm === 'function') player.warm();
        this.pausePlayer(player);
      }
    });
  }
}

if (!customElements.get('dieux-product-video')) {
  customElements.define('dieux-product-video', DieuxProductVideo);
}
